// src/auth/auth.client.ts — pagina de autentificare / înregistrare (#/autentificare, #/inregistrare).
// Parolă (POST /api/auth/login|register) + buton „Intră cu Microsoft" (redirect OAuth).

import "../styles.css"
import van from "vanjs-core"
import { current_path, get_hash_query_params } from "../lib/router.client"
import {
	login,
	register,
	forgot_password,
	reset_password,
	check_reset_token,
} from "../lib/session.client"
import { password_field } from "../lib/password.client"
import { api_get } from "../lib/api.client"

const t = van.tags

const input_cls =
	"w-full rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm outline-none " +
	"focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/20 transition"
const card_cls =
	"w-full max-w-sm bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 p-6 shadow-sm"
const page_cls =
	"min-h-[calc(100vh-3rem)] flex items-center justify-center bg-slate-50 dark:bg-slate-950 px-4 py-10"
const primary_btn_cls =
	"w-full px-4 py-2.5 rounded-lg bg-cyan-700 text-white text-sm font-semibold " +
	"hover:bg-cyan-800 disabled:opacity-60 transition cursor-pointer"

// Instanța trimite emailuri? (GET /api/health → `mail`). Fără provider, „Am uitat parola" nu are
// sens — linkul rămâne ascuns (on-premise fără Postmark: recuperarea se face prin administrator).
const mail_available = van.state(false)
let mail_probed = false
function probe_mail(): void {
	if (mail_probed) {
		return
	}
	mail_probed = true
	void api_get<{ mail?: boolean }>("/api/health").then((r) => {
		mail_available.val = r.ok && r.data.mail === true
	})
}

export function AuthPage() {
	const is_register = current_path.val === "/inregistrare"
	// Țintă de revenire după autentificare (ex. fluxul „Fă o solicitare" din harta publică).
	const return_to = get_hash_query_params().get("return") ?? ""
	// `?reset=ok` — venim de la setarea unei parole noi (pagina de reset redirectează aici).
	const after_reset = get_hash_query_params().get("reset") === "ok"
	const email = van.state("")
	const password = van.state("")
	const name = van.state("")
	const error = van.state("")
	const loading = van.state(false)
	probe_mail()

	async function submit(e: Event) {
		e.preventDefault()
		error.val = ""
		loading.val = true
		const r = is_register
			? await register(email.val.trim(), password.val, name.val.trim())
			: await login(email.val.trim(), password.val)
		loading.val = false
		if (r.ok) {
			location.hash = return_to ? "#" + return_to : "#/map"
		} else {
			error.val = r.message || "A apărut o eroare. Încearcă din nou."
		}
	}

	return t.div(
		{
			class:
				"min-h-[calc(100vh-3rem)] flex items-center justify-center bg-slate-50 dark:bg-slate-950 px-4 py-10",
		},
		t.form(
			{
				class:
					"w-full max-w-sm bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 p-6 shadow-sm",
				onsubmit: submit,
			},
			t.h1(
				{ class: "text-xl font-bold text-slate-900 dark:text-slate-100 mb-1" },
				is_register ? "Creează cont" : "Autentificare",
			),
			t.p(
				{ class: "text-sm text-slate-500 dark:text-slate-400 mb-5" },
				is_register
					? "Creează un cont ca să depui și să urmărești solicitări."
					: "Autentifică-te ca să continui.",
			),
			t.div(
				{
					class: after_reset
						? "mb-4 rounded-lg bg-emerald-50 dark:bg-emerald-950 border border-emerald-200 dark:border-emerald-800 px-3 py-2 text-sm text-emerald-800 dark:text-emerald-300"
						: "hidden",
				},
				"Parola a fost schimbată. Autentifică-te cu parola nouă.",
			),
			is_register
				? t.div(
						{ class: "mb-3" },
						t.label(
							{ class: "block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1" },
							"Nume complet",
						),
						t.input({
							class: input_cls,
							type: "text",
							required: true,
							maxlength: "120",
							// Obligatoriu: o solicitare către primărie trebuie să spună cine o depune.
							placeholder: "Ex.: Ioana Popescu",
							oninput: (e: Event) => (name.val = (e.target as HTMLInputElement).value),
						}),
					)
				: t.span({ style: "display:none" }),
			t.div(
				{ class: "mb-3" },
				t.label(
					{ class: "block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1" },
					"Email",
				),
				t.input({
					class: input_cls,
					type: "email",
					required: true,
					autocomplete: "email",
					placeholder: "nume@primarie.ro",
					oninput: (e: Event) => (email.val = (e.target as HTMLInputElement).value),
				}),
			),
			t.div(
				{ class: "mb-4" },
				t.label(
					{ class: "block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1" },
					"Parolă",
				),
				password_field({
					required: true,
					autocomplete: is_register ? "new-password" : "current-password",
					placeholder: is_register ? "minim 8 caractere" : "parola ta",
					oninput: (v) => (password.val = v),
				}).el,
			),
			t.div(
				{ class: () => (error.val ? "mb-3 text-sm text-red-600 dark:text-red-400" : "hidden") },
				() => error.val,
			),
			// „Am uitat parola" — doar la login și doar când instanța are provider de email.
			t.div(
				{ class: () => (!is_register && mail_available.val ? "mb-3 text-right" : "hidden") },
				t.a(
					{
						href: "#/recuperare-parola",
						class: "text-xs font-semibold text-cyan-700 dark:text-cyan-300 hover:underline",
					},
					"Am uitat parola",
				),
			),
			// Legal (LEGAL_CHECKLIST.md D1): acceptarea termenilor + informarea GDPR la momentul colectării
			// (art. 13 GDPR; Legea 365/2002 art. 8) — propoziție, fără bifă (decizia 16.09.2026). DOAR pe
			// înregistrare; montată static și ascunsă pe autentificare (regula Van).
			t.p(
				{
					class: is_register
						? "mb-3 text-xs text-slate-500 dark:text-slate-400 leading-relaxed"
						: "hidden",
				},
				"Prin crearea contului accepți ",
				t.a(
					{
						href: "/termeni",
						target: "_blank",
						rel: "noopener",
						class: "underline hover:text-slate-800 dark:hover:text-slate-200",
					},
					"Termenii și condițiile",
				),
				" și confirmi că ai citit ",
				t.a(
					{
						href: "/confidentialitate",
						target: "_blank",
						rel: "noopener",
						class: "underline hover:text-slate-800 dark:hover:text-slate-200",
					},
					"Politica de confidențialitate",
				),
				".",
			),
			t.button(
				{
					type: "submit",
					disabled: () => loading.val,
					class: primary_btn_cls,
				},
				() => (loading.val ? "Se procesează…" : is_register ? "Creează cont" : "Intră"),
			),
			t.div(
				{ class: "my-3 flex items-center gap-2 text-xs text-slate-400 dark:text-slate-500" },
				t.span({ class: "h-px flex-1 bg-slate-200 dark:bg-slate-700" }),
				"sau",
				t.span({ class: "h-px flex-1 bg-slate-200 dark:bg-slate-700" }),
			),
			t.a(
				{
					href: "/api/auth/oauth/microsoft",
					class:
						"w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border " +
						"border-slate-300 dark:border-slate-600 text-sm font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition",
				},
				"Intră cu Microsoft",
			),
			t.p(
				{ class: "mt-5 text-center text-xs text-slate-500 dark:text-slate-400" },
				is_register ? "Ai deja cont? " : "Nu ai cont? ",
				t.a(
					{
						href:
							(is_register ? "#/autentificare" : "#/inregistrare") +
							(return_to ? `?return=${encodeURIComponent(return_to)}` : ""),
						class: "font-semibold text-cyan-700 dark:text-cyan-300 hover:underline",
					},
					is_register ? "Autentifică-te" : "Creează unul",
				),
			),
		),
	)
}

// ─────────────────────────────────────────────
// „Am uitat parola" (#/recuperare-parola) + parolă nouă din link (#/reset-parola?t=…)
// ─────────────────────────────────────────────

/** Cererea linkului de resetare. Mesaj GENERIC la succes (serverul nu spune dacă emailul există). */
export function ForgotPasswordPage() {
	const email = van.state("")
	const error = van.state("")
	const done = van.state(false)
	const loading = van.state(false)

	async function submit(e: Event) {
		e.preventDefault()
		error.val = ""
		loading.val = true
		const r = await forgot_password(email.val.trim())
		loading.val = false
		if (r.ok) {
			done.val = true
		} else {
			error.val = r.message || "A apărut o eroare. Încearcă din nou."
		}
	}

	return t.div(
		{ class: page_cls },
		t.form(
			{ class: card_cls, onsubmit: submit },
			t.h1(
				{ class: "text-xl font-bold text-slate-900 dark:text-slate-100 mb-1" },
				"Am uitat parola",
			),
			t.p(
				{ class: "text-sm text-slate-500 dark:text-slate-400 mb-5" },
				"Scrie emailul contului și îți trimitem un link pentru o parolă nouă.",
			),
			t.div(
				{
					class: () =>
						done.val
							? "mb-4 rounded-lg bg-emerald-50 dark:bg-emerald-950 border border-emerald-200 dark:border-emerald-800 px-3 py-2 text-sm text-emerald-800 dark:text-emerald-300"
							: "hidden",
				},
				"Dacă există un cont cu acest email, ai primit un link de resetare (valabil 60 de minute). Verifică și folderul Spam.",
			),
			t.div(
				{ class: () => (done.val ? "hidden" : "mb-4") },
				t.label(
					{ class: "block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1" },
					"Email",
				),
				t.input({
					class: input_cls,
					type: "email",
					required: true,
					autocomplete: "email",
					placeholder: "nume@primarie.ro",
					oninput: (e: Event) => (email.val = (e.target as HTMLInputElement).value),
				}),
			),
			t.div(
				{ class: () => (error.val ? "mb-3 text-sm text-red-600 dark:text-red-400" : "hidden") },
				() => error.val,
			),
			t.button(
				{
					type: "submit",
					disabled: () => loading.val || done.val,
					class: () => (done.val ? "hidden" : primary_btn_cls),
				},
				() => (loading.val ? "Se trimite…" : "Trimite linkul"),
			),
			t.p(
				{ class: "mt-5 text-center text-xs text-slate-500 dark:text-slate-400" },
				t.a(
					{
						href: "#/autentificare",
						class: "font-semibold text-cyan-700 dark:text-cyan-300 hover:underline",
					},
					"Înapoi la autentificare",
				),
			),
		),
	)
}

/** Parolă nouă cu tokenul din email. La succes → #/autentificare?reset=ok. */
export function ResetPasswordPage() {
	const token = get_hash_query_params().get("t") ?? ""
	const password = van.state("")
	const confirm = van.state("")
	const error = van.state("")
	const loading = van.state(false)
	// Pre-verificare LA DESCHIDERE: un link invalid / expirat / deja folosit se anunță înainte ca
	// utilizatorul să scrie parola (altfel afla abia la submit). Cele 3 stări sunt montate STATIC și
	// comutate prin class (regula Van — nimic null dintr-o funcție reactivă).
	const link_state = van.state<"pending" | "valid" | "invalid">(token ? "pending" : "invalid")
	const link_error = van.state(token ? "" : "Linkul de resetare e incomplet.")
	function mark_invalid(message: string) {
		link_error.val = message
		link_state.val = "invalid"
	}
	if (token) {
		void check_reset_token(token).then((r) => {
			if (r.ok) {
				link_state.val = "valid"
			} else {
				mark_invalid(r.message || "Linkul de resetare e invalid sau a expirat.")
			}
		})
	}

	async function submit(e: Event) {
		e.preventDefault()
		error.val = ""
		if (password.val.length < 8) {
			error.val = "Parola trebuie să aibă minim 8 caractere."
			return
		}
		if (password.val !== confirm.val) {
			error.val = "Parolele nu coincid."
			return
		}
		loading.val = true
		const r = await reset_password(token, password.val)
		loading.val = false
		if (r.ok) {
			location.hash = "#/autentificare?reset=ok"
		} else if (/invalid|expirat/i.test(r.message ?? "")) {
			mark_invalid(r.message ?? "") // folosit între timp (alt tab) → cardul de link invalid
		} else {
			error.val = r.message || "A apărut o eroare. Încearcă din nou."
		}
	}

	const pending_card = t.div(
		{ class: `${card_cls} text-sm text-slate-500 dark:text-slate-400` },
		"Se verifică linkul…",
	)
	const invalid_card = t.div(
		{ class: card_cls },
		t.h1({ class: "text-xl font-bold text-slate-900 dark:text-slate-100 mb-2" }, "Link invalid"),
		t.p(
			{ class: "text-sm text-slate-500 dark:text-slate-400" },
			() => `${link_error.val} `,
			t.a(
				{
					href: "#/recuperare-parola",
					class: "font-semibold text-cyan-700 dark:text-cyan-300 hover:underline",
				},
				"Cere unul nou",
			),
			".",
		),
	)
	const form = t.form(
		{ class: card_cls, onsubmit: submit },
		t.h1({ class: "text-xl font-bold text-slate-900 dark:text-slate-100 mb-1" }, "Parolă nouă"),
		t.p(
			{ class: "text-sm text-slate-500 dark:text-slate-400 mb-5" },
			"Alege o parolă de minim 8 caractere.",
		),
		t.div(
			{ class: "mb-3" },
			t.label(
				{ class: "block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1" },
				"Parola nouă",
			),
			password_field({
				required: true,
				autocomplete: "new-password",
				placeholder: "minim 8 caractere",
				oninput: (v) => (password.val = v),
			}).el,
		),
		t.div(
			{ class: "mb-4" },
			t.label(
				{ class: "block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1" },
				"Confirmă parola",
			),
			password_field({
				required: true,
				autocomplete: "new-password",
				placeholder: "aceeași parolă",
				oninput: (v) => (confirm.val = v),
			}).el,
		),
		t.div(
			{ class: () => (error.val ? "mb-3 text-sm text-red-600 dark:text-red-400" : "hidden") },
			() => error.val,
		),
		t.button({ type: "submit", disabled: () => loading.val, class: primary_btn_cls }, () =>
			loading.val ? "Se salvează…" : "Salvează parola",
		),
	)

	return t.div(
		{ class: page_cls },
		t.div({ class: () => (link_state.val === "pending" ? "" : "hidden") }, pending_card),
		t.div({ class: () => (link_state.val === "invalid" ? "" : "hidden") }, invalid_card),
		t.div({ class: () => (link_state.val === "valid" ? "" : "hidden") }, form),
	)
}
